
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Switch, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { ChevronDown, GraduationCap, KanbanSquare, Plus, Users, Wallet, AppWindow } from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { APPLICATION_KEY } from "../../../Configurations/Urls";
import {
  assignApplicationToOrganization,
  deactivateOrganizationApplication,
  fetchClientsForOrgAppAccess,
  fetchOrgAppAccessContext,
  reactivateOrganizationApplication,
} from "../../../Apis/OrganizationApplicationAccess";

const ORG_COLORS = [
  "#3f6fd6", "#0d8f6f", "#7a59c2", "#d64343", "#c27a10",
  "#0e8fa8", "#8f5c0e", "#2f7a3f", "#a83f72", "#4a5fa8",
];

function colorFromStr(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return ORG_COLORS[h % ORG_COLORS.length];
}

function orgInitials(shortName, fullName) {
  const src = String(shortName || fullName || "").trim();
  if (!src) return "ORG";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function clientInitials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  return String(name || "").slice(0, 2).toUpperCase();
}

const APP_ICONS = {
  "human resources": <Users size={15} />,
  "project management": <KanbanSquare size={15} />,
  "education management": <GraduationCap size={15} />,
  "finance": <Wallet size={15} />,
};

function getAppIcon(name) {
  const key = String(name || "").toLowerCase();
  for (const [k, icon] of Object.entries(APP_ICONS)) {
    if (key.includes(k)) return icon;
  }
  return <AppWindow size={15} />;
}

function buildRows(context) {
  const rows = [];
  for (const org of context?.organizations || []) {
    for (const app of org.linked_applications || []) {
      rows.push({
        organization_id: Number(org.organization_id),
        organization_name: org.organization_name,
        organization_short_name: org.organization_short_name,
        application_id: Number(app.application_id),
        application_name: app.application_name,
        is_active: !!app.is_active,
        link_exists: true,
      });
    }
  }
  return rows.sort((a, b) => {
    const orgCmp = a.organization_name.localeCompare(b.organization_name);
    if (orgCmp !== 0) return orgCmp;
    return a.application_name.localeCompare(b.application_name);
  });
}

function countActiveOrganizationsByApplication(rows) {
  const counts = new Map();
  (rows || []).forEach((row) => {
    if (!row?.link_exists || !row.is_active) return;
    const appId = Number(row.application_id);
    counts.set(appId, (counts.get(appId) || 0) + 1);
  });
  return counts;
}

function isSoleActiveOrganizationForApplication(row, activeOrgCountByApp) {
  if (!row?.link_exists || !row.is_active) return false;
  return (activeOrgCountByApp.get(Number(row.application_id)) || 0) <= 1;
}

export default function OrganizationApplicationAccess() {
  const { userData, logout } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const navigate = useNavigate();
  const canView = Array.isArray(Permission) && Permission.includes("VIEW_ORGANIZATION_APPLICATION");
  const canAdd = Array.isArray(Permission) && Permission.includes("ADD_ORGANIZATION_APPLICATION");
  const canChangeStatus = Array.isArray(Permission) && Permission.includes("CHANGE_STATUS");
  const loggedInClientId = useMemo(() => {
    const id =
      userData?.client_id ??
      userData?.client?.client_id ??
      userData?.Client?.client_id ??
      null;
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [userData]);

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState(null);
  const [updatingKey, setUpdatingKey] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [toggleConfirmRow, setToggleConfirmRow] = useState(null);

  useEffect(() => {
    if (!canView) {
      setClients([]);
      setSelectedClientId("");
      setContext(null);
      setClientsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setClientsLoading(true);
      try {
        const list = await fetchClientsForOrgAppAccess();
        if (!cancelled) {
          const safe = list || [];
          if (loggedInClientId) {
            const mine = safe.filter((c) => Number(c.client_id) === loggedInClientId);
            setClients(mine);
            setSelectedClientId(mine.length ? String(mine[0].client_id) : "");
          } else {
            setClients(safe);
          }
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.error || err.message || "Failed to load clients.");
        }
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loggedInClientId, canView]);

  const loadContext = useCallback(async (clientId) => {
    if (!clientId) { setContext(null); return; }
    setContextLoading(true);
    setContextError(null);
    try {
      setContext(await fetchOrgAppAccessContext(clientId));
    } catch (err) {
      setContext(null);
      setContextError(err.response?.data?.error || err.message || "Failed to load organization application access.");
    } finally {
      setContextLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canView || !selectedClientId) { setContext(null); return; }
    loadContext(Number(selectedClientId));
  }, [selectedClientId, loadContext, canView]);

  const rows = useMemo(() => buildRows(context), [context]);
  const activeOrgCountByApplication = useMemo(
    () => countActiveOrganizationsByApplication(rows),
    [rows]
  );
  const clientOwnedApps = useMemo(
    () => (context?.assigned_applications || []).filter((a) => a.is_active),
    [context]
  );
  const linkedRowSet = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.link_exists) s.add(`${r.organization_id}-${r.application_id}`); });
    return s;
  }, [rows]);
  const assignableApplicationsByOrg = useMemo(() => {
    const m = new Map();
    (context?.organizations || []).forEach((org) => {
      const orgId = Number(org.organization_id);
      const apps = clientOwnedApps.filter((app) => !linkedRowSet.has(`${orgId}-${Number(app.application_id)}`));
      if (apps.length) m.set(String(orgId), apps);
    });
    return m;
  }, [context, clientOwnedApps, linkedRowSet]);
  const assignableOrganizations = useMemo(
    () => (context?.organizations || []).filter((org) => assignableApplicationsByOrg.has(String(org.organization_id))),
    [context, assignableApplicationsByOrg]
  );
  const assignableApplications = useMemo(
    () => assignableApplicationsByOrg.get(String(organizationId)) || [],
    [assignableApplicationsByOrg, organizationId]
  );

  useEffect(() => {
    if (!organizationId) return;
    if (!assignableApplicationsByOrg.has(String(organizationId))) { setOrganizationId(""); setApplicationId(""); return; }
    if (applicationId && !assignableApplications.some((app) => String(app.application_id) === String(applicationId))) {
      setApplicationId("");
    }
  }, [organizationId, applicationId, assignableApplicationsByOrg, assignableApplications]);

  const handleAssign = async () => {
    if (!canAdd) return;
    if (!selectedClientId || !organizationId || !applicationId) { toast.error("Select organization and application."); return; }
    if (linkedRowSet.has(`${organizationId}-${applicationId}`)) { toast.error("This organization is already linked to this application."); return; }
    setAssigning(true);
    try {
      const res = await assignApplicationToOrganization(Number(selectedClientId), {
        organization_id: Number(organizationId),
        application_id: Number(applicationId),
      });
      toast.success(res.message || "Organization access assigned.");
      setApplicationId("");
      await loadContext(Number(selectedClientId));
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || err.message || "Failed to assign organization access.");
    } finally {
      setAssigning(false);
    }
  };

  const requestToggleAccess = (row) => {
    if (!canChangeStatus) return;
    const soleActiveOrg = isSoleActiveOrganizationForApplication(
      row,
      activeOrgCountByApplication
    );
    if (row.is_active && soleActiveOrg) {
      toast.error(
        "Cannot deactivate the only organization assigned to this application."
      );
      return;
    }
    setToggleConfirmRow(row);
  };

  const confirmToggleAccess = async () => {
    const row = toggleConfirmRow;
    if (!row || !canChangeStatus) return;
    setToggleConfirmRow(null);

    const activeOrgId = Number(
      userData?.user_active_organization?.user_current_active_organization_id ??
        userData?.user_active_organization?.organization_id ??
        userData?.organizationuser?.organization_id ??
        userData?.organization?.organization_id ??
        0
    );
    const appKey = String(APPLICATION_KEY || "").toLowerCase();
    const appName = String(row.application_name || "").toLowerCase();
    const isCurrentAppRow =
      (appKey === "ems" && /education|ems/.test(appName)) ||
      (appKey === "pm" && /project|pm/.test(appName)) ||
      (appKey === "hr" && /human resources|human resource|hr/.test(appName));
    const isSelfOrgForCurrentApp = row.is_active && row.organization_id === activeOrgId && isCurrentAppRow;

    const key = `${row.organization_id}-${row.application_id}`;
    setUpdatingKey(key);
    try {
      const payload = { organization_id: row.organization_id, application_id: row.application_id };
      const res = row.is_active
        ? await deactivateOrganizationApplication(Number(selectedClientId), payload)
        : row.link_exists
          ? await reactivateOrganizationApplication(Number(selectedClientId), payload)
          : await assignApplicationToOrganization(Number(selectedClientId), payload);
      toast.success(res.message || "Updated.");
      if (res.force_logout || isSelfOrgForCurrentApp) {
        logout();
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      await loadContext(Number(selectedClientId));
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || err.message || "Failed to update organization application access.");
    } finally {
      setUpdatingKey("");
    }
  };

  const selectedClient = clients.find((c) => String(c.client_id) === String(selectedClientId));
  const clientName = clientsLoading
    ? "Loading..."
    : selectedClient?.client_name || (selectedClientId ? `Client #${selectedClientId}` : "No client available");

  return (
    <div className="ems-fade-up">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">Organization Application Access</h1>
          <div className="ems-page-sub">
            <span>Organization Setup</span>
            <span className="ems-dot" />
            <span>{rows.length} access grants</span>
          </div>
        </div>
      </div>
      <div className="ems-section-divider" />

      {!canView && (
        <div className="ems-card" style={{ padding: "16px 20px", marginBottom: 16, color: "var(--ems-amber)", background: "var(--ems-amber-tint, #fef9ec)", border: "1px solid var(--ems-amber)" }}>
          You do not have permission to view organization application access.
        </div>
      )}

      {/* Client card */}
      <div className="ems-card ems-aa-client">
        <label className="ems-aa-label">Client</label>
        <div className="ems-aa-client-val">
          <span
            className="ems-aa-t-avatar"
            style={{ background: selectedClient ? colorFromStr(clientName) : "#7c5cbf" }}
          >
            {clientInitials(clientName)}
          </span>
          {clientName}
        </div>
      </div>

      {canView && !contextLoading && selectedClientId && !contextError && (
        <>
          {/* Assign card */}
          {canAdd && (
            <div className="ems-card ems-aa-assign">
              <div className="ems-aa-assign-title">Assign organization to application</div>
              <div className="ems-aa-assign-row">
                <div className="ems-fm-select-wrap">
                  <select
                    className="ems-fm-input"
                    value={organizationId}
                    onChange={(e) => setOrganizationId(String(e.target.value))}
                  >
                    <option value="">Organization</option>
                    {assignableOrganizations.map((org) => (
                      <option key={org.organization_id} value={org.organization_id}>
                        {org.organization_name}
                        {org.organization_short_name ? ` (${org.organization_short_name})` : ""}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} />
                </div>
                <div className="ems-fm-select-wrap">
                  <select
                    className="ems-fm-input"
                    value={applicationId}
                    onChange={(e) => setApplicationId(String(e.target.value))}
                  >
                    <option value="">Application</option>
                    {assignableApplications.map((app) => (
                      <option key={app.application_id} value={app.application_id}>
                        {app.application_name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} />
                </div>
                <button
                  className="ems-btn ems-primary ems-aa-assign-btn"
                  onClick={handleAssign}
                  disabled={assigning || !organizationId || !applicationId}
                >
                  <Plus size={16} />
                  {assigning ? "Assigning..." : "Assign Access"}
                </button>
              </div>
            </div>
          )}

          {/* Table card */}
          {rows.length === 0 ? (
            <div className="ems-card" style={{ padding: "24px 20px", color: "var(--ems-muted)", textAlign: "center", fontSize: 14 }}>
              No organization applications found. Use "Assign Access" above to get started.
            </div>
          ) : (
            <div className="ems-card ems-table-card">
              <div className="ems-table-wrap">
                <table className="ems-data">
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th>Application</th>
                      <th style={{ width: 120 }}>Status</th>
                      {canChangeStatus && <th className="ems-num" style={{ width: 96 }}>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const key = `${row.organization_id}-${row.application_id}`;
                      const busy = updatingKey === key;
                      const soleActiveOrg = isSoleActiveOrganizationForApplication(
                        row,
                        activeOrgCountByApplication
                      );
                      const initials = orgInitials(row.organization_short_name, row.organization_name);
                      return (
                        <tr key={key}>
                          <td>
                            <div className="ems-act-cell">
                              <span className="ems-org-mark">
                                {initials}
                              </span>
                              <span className="ems-lead-name">{row.organization_name}</span>
                            </div>
                          </td>
                          <td>
                            <span className="ems-aa-app">
                              <span className="ems-aa-app-ic">{getAppIcon(row.application_name)}</span>
                              {row.application_name}
                            </span>
                          </td>
                          <td>
                            {row.is_active ? (
                              <span className="ems-tag" style={{ color: "var(--ems-green)", background: "rgba(21,168,91,.12)" }}>
                                <span className="d" style={{ background: "var(--ems-green)" }} />
                                Active
                              </span>
                            ) : (
                              <span className="ems-tag" style={{ color: "var(--ems-faint)", background: "rgba(148,163,184,.12)" }}>
                                <span className="d" style={{ background: "#94a3b8" }} />
                                Inactive
                              </span>
                            )}
                          </td>
                          {canChangeStatus && (
                            <td className="ems-num">
                              <Tooltip
                                title={
                                  soleActiveOrg
                                    ? "This is the only active organization for this application and cannot be deactivated."
                                    : row.is_active
                                    ? "Toggle to inactive"
                                    : row.link_exists
                                      ? "Toggle to active"
                                      : "Assign and activate"
                                }
                              >
                                <span>
                                  <Switch
                                    size="small"
                                    checked={row.is_active}
                                    onChange={() => requestToggleAccess(row)}
                                    disabled={busy || soleActiveOrg}
                                    color="success"
                                  />
                                </span>
                              </Tooltip>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {contextLoading && (
        <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
          <CircularProgress size={32} />
        </div>
      )}

      {contextError && (
        <div className="ems-card" style={{ padding: "16px 20px", marginTop: 12, color: "var(--ems-red)", border: "1px solid var(--ems-red-tint, #fde)" }}>
          {contextError}
        </div>
      )}

      <Dialog open={!!toggleConfirmRow} onClose={() => setToggleConfirmRow(null)}>
        <DialogTitle>Confirm status change</DialogTitle>
        <DialogContent>
          Are you sure you want to set{" "}
          <strong>{toggleConfirmRow?.organization_name}</strong> access to{" "}
          <strong>{toggleConfirmRow?.application_name}</strong> as{" "}
          <strong>{toggleConfirmRow?.is_active ? "Inactive" : "Active"}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleConfirmRow(null)} disabled={!!updatingKey}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmToggleAccess}
            disabled={!!updatingKey}
            sx={
              toggleConfirmRow?.is_active
                ? {
                    bgcolor: "#DC2626",
                    "&:hover": { bgcolor: "#B91C1C" },
                  }
                : {
                    bgcolor: "#0891B2",
                    "&:hover": { bgcolor: "#067a9a" },
                  }
            }
          >
            {updatingKey ? "Updating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}


